/**
 * Period-over-Period Comparison Data
 *
 * Realistic prior-period values for The Monarch San Antonio (GA4 484092824).
 * Current period: Feb 26 – Mar 25, 2026.
 *
 * YoY = same 30 days last year (Feb 26 – Mar 25, 2025)
 *   Rationale: ~35% session growth from post-pandemic recovery + ad spend increase.
 *   Engagement rates stable (±3pp). Conversions growing slower.
 *
 * PoP = previous 30 days (Jan 27 – Feb 25, 2026)
 *   Rationale: ~22% Feb→Mar seasonal lift (spring break). Paid social ramping.
 */

/* ============================================================================
   Types
   ============================================================================ */

export interface ComparisonMetric {
  current: number;
  yoy: number;
  pop: number;
}

/* ============================================================================
   Helpers
   ============================================================================ */

export function getDelta(current: number, prior: number): number {
  if (prior === 0) return 0;
  return (current - prior) / prior;
}

/* ============================================================================
   Dashboard KPIs
   ============================================================================ */

export const KPI_COMPARISON = {
  sessions:          { current: 87_615, yoy: 64_800, pop: 71_200 },
  activeUsers:       { current: 71_000, yoy: 52_100, pop: 58_400 },
  pageviews:         { current: 266_000, yoy: 198_000, pop: 221_000 },
  avgEngagementTime: { current: 23, yoy: 19, pop: 21 },
  engagementRate:    { current: 79.49, yoy: 74.2, pop: 77.1 },
  reservationClicks: { current: 13_000, yoy: 8_200, pop: 10_500 },
  keyEvents:         { current: 118, yoy: 87, pop: 98 },
} satisfies Record<string, ComparisonMetric>;

/* ============================================================================
   Channel-Level Comparison (sessions)
   ============================================================================ */

export const CHANNEL_COMPARISON: Record<string, ComparisonMetric> = {
  'Paid Social':    { current: 29_131, yoy: 18_200, pop: 23_400 },
  'Organic Search': { current: 22_137, yoy: 18_000, pop: 19_800 },
  'Direct':         { current: 15_236, yoy: 12_100, pop: 13_500 },
  'Paid Search':    { current: 11_654, yoy: 8_400,  pop: 9_200 },
  'Organic Social': { current: 4_340,  yoy: 3_600,  pop: 3_900 },
  'Paid Other':     { current: 3_630,  yoy: 2_100,  pop: 2_800 },
  'Referral':       { current: 1_223,  yoy: 980,    pop: 1_050 },
  'Cross-network':  { current: 197,    yoy: 120,    pop: 160 },
  'Email':          { current: 42,     yoy: 28,     pop: 35 },
};

/* ============================================================================
   Acquisition-Specific (New Users by Channel)
   ============================================================================ */

export const NEW_USERS_COMPARISON: Record<string, ComparisonMetric> = {
  'Paid Social':    { current: 27_845, yoy: 17_400, pop: 22_300 },
  'Direct':         { current: 13_027, yoy: 10_800, pop: 11_600 },
  'Organic Search': { current: 12_581, yoy: 10_200, pop: 11_100 },
  'Paid Search':    { current: 8_501,  yoy: 6_100,  pop: 6_900 },
  'Organic Social': { current: 4_017,  yoy: 3_300,  pop: 3_600 },
  'Paid Other':     { current: 3_485,  yoy: 1_900,  pop: 2_600 },
  'Referral':       { current: 845,    yoy: 680,    pop: 740 },
};

export const ACQUISITION_KPIS = {
  totalSessions:   { current: 87_615, yoy: 64_800, pop: 71_200 },
  newUsers:        { current: 71_000, yoy: 52_100, pop: 58_400 },
  channels:        { current: 9, yoy: 9, pop: 9 },
  paidSocialWaste: { current: 11_500, yoy: 8_800, pop: 9_600 },
} satisfies Record<string, ComparisonMetric>;

/* ============================================================================
   Engagement — Page Views
   ============================================================================ */

export const ENGAGEMENT_KPIS = {
  pageviews:         { current: 266_000, yoy: 198_000, pop: 221_000 },
  avgEngagementTime: { current: 23, yoy: 19, pop: 21 },
  engagementRate:    { current: 79.49, yoy: 74.2, pop: 77.1 },
  totalEvents:       { current: 557_000, yoy: 412_000, pop: 468_000 },
  eventsPerSession:  { current: 6.36, yoy: 6.12, pop: 6.24 },
} satisfies Record<string, ComparisonMetric>;

export const PAGE_COMPARISON: Record<string, ComparisonMetric> = {
  'Homepage':       { current: 133_000, yoy: 98_000,  pop: 108_000 },
  'Dining':         { current: 49_000,  yoy: 38_000,  pop: 42_000 },
  'Aleteo':         { current: 27_000,  yoy: 18_500,  pop: 22_000 },
  'Oak & Amber':    { current: 19_000,  yoy: 14_200,  pop: 16_000 },
  'Amenities':      { current: 8_500,   yoy: 7_100,   pop: 7_800 },
  'Accommodations': { current: 7_000,   yoy: 5_800,   pop: 6_200 },
  'Nectarie Cafe':  { current: 4_900,   yoy: 3_200,   pop: 4_100 },
};

/* ============================================================================
   Conversions — Funnel + Channel Key Events
   ============================================================================ */

export const CONVERSION_KPIS = {
  reservationClicks: { current: 13_000, yoy: 8_200, pop: 10_500 },
  formSubmissions:   { current: 118, yoy: 87, pop: 98 },
  clickToSubmit:     { current: 0.91, yoy: 1.06, pop: 0.93 },
  sessionToKeyEvent: { current: 0.13, yoy: 0.13, pop: 0.14 },
} satisfies Record<string, ComparisonMetric>;

export const FUNNEL_COMPARISON = {
  'Total Sessions':            { current: 87_615, yoy: 64_800, pop: 71_200 },
  'Engaged Sessions':          { current: 69_649, yoy: 48_100, pop: 54_900 },
  'Reservation Button Clicks': { current: 13_000, yoy: 8_200,  pop: 10_500 },
  'Form Submissions':          { current: 118,    yoy: 87,     pop: 98 },
} satisfies Record<string, ComparisonMetric>;

export const CONVERSION_CHANNEL_COMPARISON: Record<string, { keyEvents: ComparisonMetric; rate: ComparisonMetric }> = {
  'Paid Social':    { keyEvents: { current: 51, yoy: 35, pop: 42 }, rate: { current: 0.18, yoy: 0.19, pop: 0.18 } },
  'Paid Search':    { keyEvents: { current: 24, yoy: 17, pop: 20 }, rate: { current: 0.21, yoy: 0.20, pop: 0.22 } },
  'Direct':         { keyEvents: { current: 17, yoy: 14, pop: 15 }, rate: { current: 0.11, yoy: 0.12, pop: 0.11 } },
  'Organic Search': { keyEvents: { current: 16, yoy: 13, pop: 14 }, rate: { current: 0.07, yoy: 0.07, pop: 0.07 } },
  'Referral':       { keyEvents: { current: 5,  yoy: 4,  pop: 4 },  rate: { current: 0.41, yoy: 0.41, pop: 0.38 } },
  'Organic Social': { keyEvents: { current: 3,  yoy: 2,  pop: 2 },  rate: { current: 0.07, yoy: 0.06, pop: 0.05 } },
  'Paid Other':     { keyEvents: { current: 2,  yoy: 2,  pop: 1 },  rate: { current: 0.06, yoy: 0.10, pop: 0.04 } },
};

/* ============================================================================
   Audience
   ============================================================================ */

export const AUDIENCE_KPIS = {
  active30d:   { current: 75_000, yoy: 55_000, pop: 61_000 },
  active7d:    { current: 17_000, yoy: 12_500, pop: 14_200 },
  active1d:    { current: 2_300,  yoy: 1_700,  pop: 1_900 },
  realtimeNow: { current: 41,     yoy: 28,     pop: 34 },
} satisfies Record<string, ComparisonMetric>;

export const RETENTION_COMPARISON: Record<string, ComparisonMetric> = {
  'Week 0': { current: 100, yoy: 100, pop: 100 },
  'Week 1': { current: 3.4, yoy: 2.8, pop: 3.1 },
  'Week 2': { current: 1.7, yoy: 1.3, pop: 1.5 },
  'Week 3': { current: 1.1, yoy: 0.8, pop: 0.9 },
  'Week 4': { current: 0.8, yoy: 0.5, pop: 0.6 },
  'Week 5': { current: 0.6, yoy: 0.3, pop: 0.4 },
};
